import { useState } from "react";
import Loader from "@/components/views/Loader";
import { Switch } from "@headlessui/react";
import Formfield from "@/components/forms/Field";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { filterNavItemsByRole } from "@/components/utils/adminUtils";

import { Upload } from "@/components/forms/Upload";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_MUTATION } from "@/graphql/mutations";
import { useDispatch } from "react-redux";
import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { secondaryNavigationData } from "@/components/navigation/navigationData";
import { classNames } from "@/components/utils/functions";

// Helper to set current section and filter by user role
const getNavWithCurrent = (currentName: string, userRole?: string) => {
  // First filter by role, then mark current section
  const filteredNav = filterNavItemsByRole(secondaryNavigationData, userRole);
  return filteredNav.map((item) => ({
    ...item,
    current: item.name === currentName,
  }));
};

function AvatarUploadSection({ user }: { user: any }) {
  const [avatarUrl, setAvatarUrl] = useState(user.avatar || "");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const dispatch = useDispatch();

  const handleUpload = async (urls: string[]) => {
    if (!urls || urls.length === 0) return;
    setUpdating(true);
    setError(null);
    setSuccess(false);
    try {
      const { data } = await updateUser({
        variables: { fieldName: "avatar", fieldValue: urls[0] },
      });
      if (data?.updateUser?.avatar) {
        setAvatarUrl(data.updateUser.avatar);
        setSuccess(true);
        // Optionally update user in Redux store if needed
        dispatch({ type: "SET_USER_DETAILS", payload: data.updateUser });
      } else {
        setError("Failed to update avatar.");
      }
    } catch (e: any) {
      setError(e.message || "Failed to update avatar.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-4 mb-2">
        {avatarUrl && (
          <img
            src={avatarUrl}
            alt="Avatar preview"
            className="h-16 w-16 rounded-full border object-cover"
          />
        )}
        <Upload
          multiple={false}
          onUpload={handleUpload}
          label="Upload avatar"
        />
      </div>
      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
      {success && (
        <div className="text-green-600 text-sm mt-1">Avatar updated!</div>
      )}
      {updating && (
        <div className="text-gray-500 text-sm mt-1">Updating...</div>
      )}
    </div>
  );
}

export default function Settings() {
  const user = useSelector((state: RootState) => state.user);
  const [automaticTimezoneEnabled, setAutomaticTimezoneEnabled] =
    useState(true);

  // Show loader if user data is not yet loaded
  if (!user || Object.keys(user).length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <main className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-screen">
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">General Settings</h1>

        <aside className="lg:w-1/5">
          <SecondaryNavigation
            items={getNavWithCurrent("General", user?.role?.name)}
          />
        </aside>

        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {/* Profile Section */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
                Profile
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-300">
                Update your personal details. Changes to your email address will
                apply the next time you log in. {user.email}
              </p>
              <div className="flex justify-center items-center mt-8">
                <AvatarUploadSection user={user} />
              </div>
              <dl className="mt-6 space-y-6 divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700 text-sm leading-6">
                <Formfield
                  fieldName="name"
                  fieldType="text"
                  placeholder="Joe Bloggs"
                  fieldLabel="Full Name"
                  fieldValue={user.name}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <Formfield
                  fieldName="email"
                  fieldType="email"
                  placeholder="me@example.com"
                  fieldLabel="Email"
                  fieldValue={user.email}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <Formfield
                  fieldName="title"
                  fieldType="text"
                  placeholder="CTO"
                  fieldLabel="Title"
                  fieldValue={user.title}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <Formfield
                  fieldName="role"
                  fieldType="text"
                  placeholder="User"
                  fieldLabel="Role"
                  fieldValue={user.role?.name}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </dl>
            </section>

            {/* Language and Dates Section */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
                Language and Dates
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-300">
                Choose your preferred date format and toggle the automatic
                timezone option.
              </p>
              <dl className="mt-6 space-y-6 divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700 text-sm leading-6">
                <Formfield
                  fieldName="date_format"
                  fieldType="text"
                  placeholder="DD-MM-YYYY"
                  fieldLabel="Date Format"
                  fieldValue={user.dateFormat}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <Switch.Group as="div" className="flex pt-6">
                  <Switch.Label
                    as="dt"
                    className="flex-none pr-6 font-medium text-gray-900 dark:text-gray-100 sm:w-64"
                    passive
                  >
                    Automatic timezone
                  </Switch.Label>
                  <dd className="flex flex-auto items-center justify-end">
                    <Switch
                      checked={automaticTimezoneEnabled}
                      onChange={setAutomaticTimezoneEnabled}
                      className={classNames(
                        automaticTimezoneEnabled
                          ? "bg-indigo-600"
                          : "bg-gray-200 dark:bg-gray-700",
                        "flex w-8 cursor-pointer rounded-full p-px ring-1 ring-inset ring-gray-900/5 dark:ring-gray-700 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          automaticTimezoneEnabled
                            ? "translate-x-3.5"
                            : "translate-x-0",
                          "h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 transition duration-200 ease-in-out"
                        )}
                      />
                    </Switch>
                  </dd>
                </Switch.Group>
              </dl>
            </section>
          </div>
        </main>
      </div>
    </main>
  );
}
